import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import { Flame, Award, GraduationCap, Crown } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { makeApi } from "@/lib/api";
import { BADGES } from "@/lib/badges";
import { DeleteButton } from "@/pages/Legal";
import { isAndroidApp } from "@/lib/platform";

function TeacherApply({ api, me }) {
  const [req, setReq] = useState(null);
  const [school, setSchool] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => { api.get("/teacher/request").then((r) => setReq(r.data)); }, [api]);

  if (me.role === "teacher" || me.role === "admin") {
    return <p className="font-bold" data-testid="teacher-verified">You're a verified instructor. <Link to="/teach" className="text-[var(--tp-primary)] underline">Open the Instructor Portal</Link></p>;
  }
  if (req?.status === "pending") return <p className="font-bold" data-testid="teacher-pending">Your instructor request is waiting for admin review.</p>;

  const apply = async () => {
    if (!school.trim()) return toast.error("Tell us your school or organisation");
    try { const r = await api.post("/teacher/apply", { school, message }); setReq(r.data); toast.success("Request sent!"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3">
      {req?.status === "rejected" && <p className="text-sm text-[#FF4B4B] font-bold">Your last request wasn't approved. You can apply again.</p>}
      <input value={school} onChange={(e) => setSchool(e.target.value)} placeholder="School / organisation" data-testid="teacher-school"
        className="w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold" />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="What do you teach? (helps us verify you)" data-testid="teacher-message"
        className="w-full px-4 py-3 rounded-xl border-[3px] border-[#374151] font-bold" />
      <button onClick={apply} data-testid="teacher-apply-btn"
        className="px-5 py-2.5 rounded-full border-[3px] border-[#374151] bg-[var(--tp-primary)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
        I'm a teacher — request access
      </button>
    </div>
  );
}

export default function Profile() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/me").then((r) => setMe(r.data)); }, [api]);
  if (!me) return <><Nav /><p className="p-8">Loading…</p></>;
  const owned = new Set(me.badges || []);

  return (
    <>
      <Nav />
      <main className="max-w-4xl mx-auto px-4 py-10 space-y-8">
        <h1 className="text-4xl font-black tracking-tight">{me.name || "My profile"}</h1>

        <section className="sticker-card p-6 flex items-center gap-6 flex-wrap" style={{ background: "#FFF0E6" }}>
          <Flame size={56} fill="#F97316" color="#F97316" strokeWidth={2.5} />
          <div>
            <p className="text-5xl font-black" data-testid="profile-streak">{me.streak || 0}</p>
            <p className="font-bold text-slate-700">day streak • best {me.best_streak || 0}</p>
          </div>
          <p className="text-slate-600 ml-auto max-w-xs">Finish at least one quiz every day to keep your streak rolling.</p>
        </section>

        <section>
          <h2 className="text-2xl font-black mb-4 flex items-center gap-2"><Award strokeWidth={2.5} /> Badges</h2>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="badges-grid">
            {BADGES.map((b) => {
              const has = owned.has(b.id);
              return (
                <div key={b.id} data-testid={`badge-${b.id}`} className={`sticker-card p-5 text-center ${has ? "" : "opacity-50 grayscale"}`}
                  style={{ background: has ? b.color : "#fff" }}>
                  <div className="mx-auto w-16 h-16 rounded-full border-[3px] border-[#374151] bg-white flex items-center justify-center font-black text-xl">{b.days}</div>
                  <p className="mt-3 font-black text-lg">{b.name}</p>
                  <p className="text-sm font-bold">{has ? "Unlocked!" : b.blurb}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="sticker-card p-6">
          <h2 className="text-2xl font-black mb-2 flex items-center gap-2"><Crown strokeWidth={2.5} /> Membership</h2>
          {me.subscribed ? <p className="font-bold text-[var(--tp-success)]" data-testid="membership-status">TP Supporter — thank you!</p> : isAndroidApp() ? (
            <p className="font-bold" data-testid="membership-status">Free player.</p>
          ) : (
            <p className="font-bold" data-testid="membership-status">Free player. <Link to="/pricing" className="text-[var(--tp-primary)] underline">Go Pro for $2.99/mo</Link></p>
          )}
        </section>

        <section className="sticker-card p-6">
          <h2 className="text-2xl font-black mb-3 flex items-center gap-2"><GraduationCap strokeWidth={2.5} /> Instructors</h2>
          <TeacherApply api={api} me={me} />
        </section>

        <section className="sticker-card p-6" data-testid="danger-zone">
          <h2 className="text-2xl font-black mb-2">Delete account</h2>
          <p className="text-slate-600 mb-4">Permanently remove your account and all of its data. <Link to="/delete-account" className="underline font-bold">What gets deleted?</Link></p>
          <DeleteButton />
        </section>
      </main>
    </>
  );
}
